import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";

export type RangePreset = "24h" | "7d" | "30d" | "90d" | "custom";

export interface DateRangeValue {
  from: Date;
  to: Date;
  preset: RangePreset;
}

const PRESETS: { id: Exclude<RangePreset, "custom">; label: string; hours: number }[] = [
  { id: "24h", label: "24h", hours: 24 },
  { id: "7d", label: "7d", hours: 24 * 7 },
  { id: "30d", label: "30d", hours: 24 * 30 },
  { id: "90d", label: "90d", hours: 24 * 90 },
];

export function presetRange(preset: Exclude<RangePreset, "custom">): DateRangeValue {
  const to = new Date();
  const from = new Date(to.getTime() - PRESETS.find((p) => p.id === preset)!.hours * 60 * 60 * 1000);
  return { from, to, preset };
}

export function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>({ from: value.from, to: value.to });

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {PRESETS.map((p) => (
        <Button
          key={p.id}
          size="sm"
          variant={value.preset === p.id ? "default" : "outline"}
          className="h-8 px-2.5 text-xs"
          onClick={() => onChange(presetRange(p.id))}
        >
          {p.label}
        </Button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant={value.preset === "custom" ? "default" : "outline"}
            className={cn("h-8 px-2.5 text-xs gap-1.5")}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {value.preset === "custom"
              ? `${format(value.from, "MMM d")} – ${format(value.to, "MMM d")}`
              : "Custom"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="range"
            selected={draft}
            onSelect={setDraft}
            numberOfMonths={2}
            className={cn("p-3 pointer-events-auto")}
          />
          <div className="flex justify-end gap-2 border-t p-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!draft?.from || !draft?.to}
              onClick={() => {
                if (!draft?.from || !draft?.to) return;
                const from = new Date(draft.from);
                from.setHours(0, 0, 0, 0);
                const to = new Date(draft.to);
                to.setHours(23, 59, 59, 999);
                onChange({ from, to, preset: "custom" });
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
